/**
 * Pure TEA update for the ProseMirror find bar (#249): query, replace text,
 * case / whole-word toggles, and the search commands it runs.
 */
import { assertNever } from "../../lib/assertNever";
import type { UpdateFn } from "../../lib/tea";
import type { ProseSearchCommand, ProseSearchQuery } from "./proseEditor";

export type ProseFindBarModel = ProseSearchQuery & {
  readonly expanded: boolean;
};

export const initialProseFindBarModel: ProseFindBarModel = {
  search: "",
  replace: "",
  caseSensitive: false,
  wholeWord: false,
  expanded: false,
};

export type ProseFindBarMsg =
  | { readonly type: "Open"; readonly seed: string }
  | { readonly type: "SearchChanged"; readonly value: string }
  | { readonly type: "ReplaceChanged"; readonly value: string }
  | { readonly type: "CaseToggled" }
  | { readonly type: "WordToggled" }
  | { readonly type: "ExpandToggled" }
  | { readonly type: "Run"; readonly command: ProseSearchCommand }
  | { readonly type: "Close" };

export type ProseFindBarCmd =
  | { readonly kind: "query"; readonly query: ProseSearchQuery }
  | { readonly kind: "run"; readonly command: ProseSearchCommand }
  | { readonly kind: "focusSearch" }
  | { readonly kind: "focusReplace" }
  | { readonly kind: "close" };

function query(model: ProseFindBarModel): ProseSearchQuery {
  return {
    search: model.search,
    replace: model.replace,
    caseSensitive: model.caseSensitive,
    wholeWord: model.wholeWord,
  };
}

function withQuery(
  model: ProseFindBarModel,
): readonly [ProseFindBarModel, readonly ProseFindBarCmd[]] {
  return [model, [{ kind: "query", query: query(model) }]];
}

export function updateProseFindBar(
  model: ProseFindBarModel,
  msg: ProseFindBarMsg,
): readonly [ProseFindBarModel, readonly ProseFindBarCmd[]] {
  switch (msg.type) {
    case "Open": {
      // A selected word seeds the search; otherwise keep the last query.
      const next = msg.seed === "" ? model : { ...model, search: msg.seed };
      return [
        next,
        [{ kind: "query", query: query(next) }, { kind: "focusSearch" }],
      ];
    }
    case "SearchChanged":
      return withQuery({ ...model, search: msg.value });
    case "ReplaceChanged":
      return withQuery({ ...model, replace: msg.value });
    case "CaseToggled":
      return withQuery({
        ...model,
        caseSensitive: model.caseSensitive === false,
      });
    case "WordToggled":
      return withQuery({ ...model, wholeWord: model.wholeWord === false });
    case "ExpandToggled": {
      const expanded = model.expanded === false;
      return [
        { ...model, expanded },
        expanded ? [{ kind: "focusReplace" }] : [],
      ];
    }
    case "Run":
      return [model, [{ kind: "run", command: msg.command }]];
    case "Close":
      return [model, [{ kind: "close" }]];
    default:
      return assertNever(msg);
  }
}

export const updateProseFindBarFn: UpdateFn<
  ProseFindBarModel,
  ProseFindBarMsg,
  ProseFindBarCmd
> = updateProseFindBar;
